import clsx from 'clsx'
import React, { useRef } from 'react'

import { useThemeClass } from '../Theme'
import { createUseFeatureDeepLink } from '../useFeatureDeepLink'
import { KeyCodes } from '../utils/keyboard'
import { ColorKinds, COLORS, KINDS, SIZES, SizesKinds, TabKinds, TABS_DISPLAY_NAME } from './constants'
import styles from './style.module.scss'
import { Tab, TabProps } from './Tab'
import { TabContext } from './TabContext'
import { TabPanel } from './TabPanel'
import { DataItem, ValueType } from './types'

export interface TabsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  kind?: TabKinds
  size?: SizesKinds
  color?: ColorKinds
  stretch?: boolean
  value: ValueType
  onChange: (value: ValueType) => void
  animation?: boolean
  dataSource?: DataItem[]
  testNameSpace?: string
  children?: Array<React.ReactElement<TabProps>> | React.ReactElement<TabProps>
}

const BaseTabs = React.forwardRef<HTMLDivElement, TabsProps>((props: TabsProps, ref) => {
  const themeClass = useThemeClass()
  const {
    kind = KINDS.DEFAULT,
    size = SIZES.LARGE,
    color = COLORS.PRIMARY,
    testNameSpace = '',
    stretch = false,
    value,
    onChange,
    animation = true,
    children,
  } = props
  const menuRef = useRef<HTMLUListElement | null>(null)

  const tabsBarClassNames = clsx(styles.tabsBar, styles[`tabsBar${color}`], kind === KINDS.PILL && styles.tabsBarPill, styles[`${themeClass}`])

  const tabOnChange = (newVal: ValueType): void => {
    if (value === newVal) {
      return
    }

    onChange(newVal)
  }

  const setFocusToFirstItem = (): void => {
    const menuBarElement = menuRef.current
    if (menuBarElement == null || menuBarElement.firstElementChild == null) {
      return
    }
    ;(menuBarElement.firstElementChild as HTMLElement).focus()
  }

  const setFocusToLastItem = (): void => {
    const menuBarElement = menuRef.current
    if (menuBarElement == null || menuBarElement.lastElementChild == null) {
      return
    }
    ;(menuBarElement.lastElementChild as HTMLElement).focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLUListElement>): void => {
    const element = e.target as HTMLElement
    if (element.nodeName !== 'LI') {
      return
    }

    if ([KeyCodes.ArrowLeft, KeyCodes.ArrowUp, KeyCodes.Left, KeyCodes.Up].includes(e.key as KeyCodes)) {
      const prevSiblingElement = element.previousElementSibling
      prevSiblingElement === null ? setFocusToLastItem() : (prevSiblingElement as HTMLElement).focus()
    } else if ([KeyCodes.ArrowRight, KeyCodes.ArrowDown, KeyCodes.Right, KeyCodes.Down].includes(e.key as KeyCodes)) {
      const nextSiblingElement = element.nextElementSibling
      nextSiblingElement === null ? setFocusToFirstItem() : (nextSiblingElement as HTMLElement).focus()
    } else if ([KeyCodes.Enter, KeyCodes.Space].includes(e.key as KeyCodes)) {
      element.click()
    }
  }

  const renderChildren = (): React.ReactNode => {
    if ('dataSource' in props && Array.isArray(props.dataSource)) {
      return props.dataSource.map((item) => (
        <Tab key={item.value} value={item.value}>
          {item.label}
        </Tab>
      ))
    }

    return React.Children.map(children, (child) => {
      const childElement = child as React.FunctionComponentElement<TabProps>
      const childElementName = childElement.type?.displayName ?? 'undefined'

      if (childElementName !== 'Tab') {
        return new Error(`${TABS_DISPLAY_NAME} children should be a type of Tab, found children: ${childElementName}`)
      }

      return React.cloneElement(childElement)
    }) as React.ReactNode
  }

  return (
    <TabContext.Provider value={{ value, kind, color, size, stretch, onChange: tabOnChange, animation }}>
      <div ref={ref} className={styles.tabs} {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-tabs` })}>
        <ul ref={menuRef} role='tablist' className={tabsBarClassNames} onKeyDown={handleKeyDown}>
          {renderChildren()}
        </ul>
      </div>
    </TabContext.Provider>
  )
})

BaseTabs.displayName = TABS_DISPLAY_NAME

type ITab = typeof BaseTabs & {
  Item: typeof Tab
  Panel: typeof TabPanel
}

export const Tabs = BaseTabs as ITab
Tabs.Item = Tab
Tabs.Panel = TabPanel

export const useTabDeepLink = createUseFeatureDeepLink('tab')
